// @architect-approved: HARDCODED_COLORS - Semi-transparent modal backdrop (rgba(0,0,0,0.5)) has no theme-token equivalent

// @architect-approved: independent panels - Platform-wide toolbar loads genuinely separate
// data feeds (auth/org context, error reporting, notifications, etc.) that are displayed
// in independent widgets, not merged into one consolidated view. Each endpoint owns its
// own panel; there is no client-side join to push into a DB view/SP.

/**
 * PlatformHeader.tsx — Single Source of Truth for Org Context
 * ═══════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE: Platform-wide toolbar with two-state collapse
 *
 * PURPOSE:
 * - Load JWT validation + user org associations + roles ONCE at app mount
 * - Store in Redux global state (auth slice)
 * - Provide collapsible toolbar with org/role dropdowns
 * - All modules consume org context from Redux (NO duplicate API calls)
 *
 * TWO-STATE COLLAPSE:
 * - COLLAPSED (32px):  Handle bar + org chip + username + controls
 * - EXPANDED (56px):   Full controls with org/role dropdowns + filters
 * - Default: EXPANDED (user must have org to access platform)
 *
 * TUMBLER LOCK PATTERN:
 * - Direct route mounts → PlatformHeader validates JWT → loads org context
 * - Sets activeOrganizationUUID in Redux once (cached in localStorage)
 * - All child components read from Redux (single source of truth)
 * - Database SP controllers filter by @SAAS_Org_UUID (not client regex)
 *
 * PREVENTS AGENT ANTI-PATTERN:
 * - NO getUserOrgAssociations() in child modules
 * - NO duplicate org dropdowns in tabs
 * - NO context leakage across components
 *
 * Created: 2026-06-21 (Restoration of original prototype pattern)
 * Simplified: 2026-06-29 (Two-state collapse, default expanded)
 */

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import ChatIcon from '@mui/icons-material/Chat';
import ElevatorIcon from '@mui/icons-material/Elevator';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import HomeIcon from '@mui/icons-material/Home';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import MouseIcon from '@mui/icons-material/Mouse';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import {
    Autocomplete,
    Avatar,
    Badge,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { getEncodedRoute } from '../utils/componentLoader';
import { navigateToAppDefault, navigateToDirectory } from '../utils/directoryNavigation';
import { toast } from 'react-toastify';
import { apiRequest, apiRequestBySeq } from '../utils/apiService';
import { clearActiveError } from '../app/redux/diagnostics/diagnosticsSlice';
import { captureRouteContext } from '../utils/routeContext';
import TimeStudyDashboard from '../TGC/RegisteredTabs/scr_tgc_10979_timestudy_dashboard_overlay';
import ChatPanel from '../TGC/RegisteredTabs/scr_tgc_10639_chat_panel';
import PhoneTool from '../TGC/RegisteredTabs/scr_tgc_10989_phone_tool';
import AccessRequestModal from '../TGC/RegisteredTabs/scr_tgc_10849_quick_help_atm';
import { useChat } from '../utils/useChat';
import type { RootState } from '../app/store';
import {
    setActiveOrganizationUUID,
    setActiveOrgRoleId,
    setPfUser,
    setUserOrganizations,
    setUserRoleAssignments,
    setSuperAdminStatus,
    logoutAction,
    type Organization,
    type RoleAssignment,
} from '../app/redux/auth/authSlice';
import { SecureStorage } from '../utils/SecureStorage';

interface OrgAssociation {
    org_pfuser_association_uuid: string;
    organization_uuid: string;
    legal_name: string;
    dba_name?: string;
    org_pfuser_association_status_id: number;
    is_organization_admin?: boolean;
    is_organization_superadmin?: boolean;
    is_organization_supervisor?: boolean;
    association_start_date?: string;
    association_end_date?: string | null;
}

const PlatformHeader: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Marketing site routes (mkt_* prefix, e.g. /mkt_tgc_10022_techgacity_website) are
    // public-facing company websites embedded in the platform shell - they have no
    // concept of "organization" or "org role", so the Organization/Role selectors (and
    // their collapsed-state org chip) are hidden on these routes. Every mkt_ component's
    // directRoute/friendlyRoute begins with its own TSX prefix (see generated-routes.json),
    // so checking the path prefix is sufficient - no manifest lookup needed.
    const isMarketingSite = location.pathname.startsWith('/mkt_');
    const {
        isAuthenticated,
        pfUser,
        activeOrganizationUUID,
        activeOrgRoleId,
        userOrganizations,
        isSuperAdmin,
        userRoleAssignments,
    } = useSelector((state: RootState) => state.auth);

    // Auto-detected error state (diagnosticsSlice) - separate concern from auth/org.
    // A failing API call anywhere in the app sets this; it does NOT mean auth/org
    // context is wrong (see PATTERN_REDUX_AUTH_DEPENDENCY_STANDARD.md).
    const activeError = useSelector((state: RootState) => state.diagnostics.activeError);

    // Two-state collapse: 'collapsed' (32px handle bar) | 'expanded' (56px full controls)
    // ✅ Default to 'expanded' - user must have org to access platform
    // 🔒 ARCHITECTURE: isExpanded is PURE UI STATE ONLY
    //    - Does NOT block data loading
    //    - Does NOT affect Redux state
    //    - Only controls visibility of dropdown selector
    //    - Data loads based on JWT/pfUser, independent of UI visibility
    const [isExpanded, setIsExpanded] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Error reporting menu state
    const [errorMenuAnchor, setErrorMenuAnchor] = useState<null | HTMLElement>(null);
    const errorMenuOpen = Boolean(errorMenuAnchor);

    // Confirm dialog: shown when clicking the icon while an auto-detected error is active
    const [confirmReportOpen, setConfirmReportOpen] = useState(false);

    // Feature request dialog: free-text "add a field", "change this label", etc.
    const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
    const [featureRequestText, setFeatureRequestText] = useState('');

    // Help ATM Menu: shared self-service dialog (sign up, forgot key, guest, QR code, etc.)
    // Reused here so non-SuperAdmin users clicking the bug icon (with no auto-detected error)
    // land on something useful instead of a dead-end toast. Same component the floating Help
    // button uses on the directory host (nav_tgc_10000) - single source, no duplicated content.
    const [helpMenuOpen, setHelpMenuOpen] = useState(false);

    // User profile photo URL (TODO: Load from user profile API)
    const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);

    // Profile avatar dropdown: My Profile & Settings / Reset Password / Logout
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
    const profileMenuOpen = Boolean(profileMenuAnchor);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

    // TimeStudy dashboard overlay state
    const [timeStudyDashboardOpen, setTimeStudyDashboardOpen] = useState(false);

    // Chat panel overlay state — single global instance, driven by Redux (chatSlice).
    // PlatformHeader is the app-shell mount point; every other component opens this
    // via useChat().openChat() instead of rendering its own <ChatPanel>.
    const { isChatOpen, chatFilterContext, openChat, closeChat } = useChat();

    // Virtual Office Phone overlay state
    const [phoneToolOpen, setPhoneToolOpen] = useState(false);

    // Header icon unread badges (2026-10-06): voicemail inbox count via
    // TEL.sp_Voice_Message_Controller @Manage=7 (tsxbound router 10874-1) and
    // chat unread count via the new CHAT.sp_Messages_Controller @Manage=8 op
    // (standalone /api/chat-messages router). Fetched on mount once pfUser is
    // known, and refreshed whenever the corresponding overlay closes so the
    // badge doesn't go stale while the user is inside Phone Tool/Chat.
    const [unreadVoicemailCount, setUnreadVoicemailCount] = useState(0);
    const [unreadChatCount, setUnreadChatCount] = useState(0);

    const fetchUnreadVoicemailCount = useCallback(async () => {
        if (!pfUser?.user_uuid) return;
        try {
            const res: any = await apiRequestBySeq(10874, 1, {
                manage: 7,
                to_pfuser_uuid: pfUser.user_uuid,
            });
            setUnreadVoicemailCount(res?.stats?.unread_count ?? res?.stats?.Unread_Count ?? 0);
        } catch (err) {
            console.error('[PlatformHeader] Failed to fetch voicemail unread count:', err);
        }
    }, [pfUser?.user_uuid]);

    const fetchUnreadChatCount = useCallback(async () => {
        if (!pfUser?.user_uuid) return;
        try {
            // Trailing slash required - router is defined as @router.post("/") under
            // prefix /api/chat-messages, so the real path is /api/chat-messages/.
            // Without it, every call ate an avoidable 307 redirect round-trip on
            // every page mount, platform-wide (confirmed live, 2026-10-12).
            const res: any = await apiRequest('/api/chat-messages/', 'POST', {
                manage: 8,
                pfuser_uuid: pfUser.user_uuid,
            });
            setUnreadChatCount(res?.data?.[0]?.unread_count ?? 0);
        } catch (err) {
            console.error('[PlatformHeader] Failed to fetch chat unread count:', err);
        }
    }, [pfUser?.user_uuid]);

    useEffect(() => {
        fetchUnreadVoicemailCount();
        fetchUnreadChatCount();
    }, [fetchUnreadVoicemailCount, fetchUnreadChatCount]);

    const handleClosePhoneTool = () => {
        setPhoneToolOpen(false);
        fetchUnreadVoicemailCount();
    };

    const handleCloseChat = () => {
        closeChat();
        fetchUnreadChatCount();
    };

    /**
     * GOLD STANDARD: Load ALL user dependencies at app mount
     * Called ONCE per session, stores result in Redux
     * LOCKED: Any new auth dependency must follow this pattern
     *
     * 🔒 AUTHENTICATION PERSISTENCE ARCHITECTURE:
     * 1. JWT persisted in localStorage (managed by useAuth hook)
     * 2. On page reload: isAuthenticated=true (JWT validated), but Redux is empty
     * 3. This effect runs ONCE to fetch org associations + roles
     * 4. Stores in Redux global state (single source of truth)
     * 5. ALL grids trust Redux state for org filter
     * 6. Dropdown visibility (isExpanded) does NOT block data loading
     * 7. Grids load based on Redux pfUser/activeOrganizationUUID, not UI visibility
     */
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        // ✅ CRITICAL FIX (2026-07-01): Restore pfUser from localStorage on page reload
        // Login stores pfUser to SecureStorage (username, user_uuid, pfuser_id)
        // Redux doesn't persist across reloads - must restore from localStorage
        if (!pfUser?.user_uuid) {
            const storedUserUUID = SecureStorage.getPfuserUuid();
            const storedPfuserId = SecureStorage.getPfuserId();
            const storedUsername = SecureStorage.getUsername();

            if (storedUserUUID) {
                console.log('[PlatformHeader] Restoring pfUser from localStorage (page reload):', {
                    user_uuid: storedUserUUID,
                    pfuser_id: storedPfuserId,
                    username: storedUsername,
                });
                dispatch(
                    setPfUser({
                        user_uuid: storedUserUUID,
                        pfuser_id: storedPfuserId || 0,
                        username: storedUsername || 'Unknown',
                    }),
                );
                // Note: useEffect will re-run after Redux update, then proceed to load orgs
                return;
            } else {
                console.warn(
                    '[PlatformHeader] No pfUser in Redux or localStorage - JWT may be invalid',
                );
                return;
            }
        }

        // 🔒 ARCHITECTURAL FIX: JWT persisted but pfUser lost on page reload
        // On reload: isAuthenticated=true (JWT in localStorage) but pfUser=null (Redux doesn't persist)
        // Solution: Decode JWT to restore pfUser, or proceed with JWT-based API calls
        // The org associations API will validate JWT and return data
        // TODO: Add JWT decode or /api/auth/validate endpoint to restore pfUser properly

        // 🔒 CRITICAL FIX (2026-07-01): Check BOTH pfUser AND userOrganizations
        // On page reload: JWT persists but pfUser=null in Redux (doesn't persist)
        // Must reload if EITHER dependency is missing, not just orgs
        if (userOrganizations.length > 0 && pfUser?.user_uuid) {
            console.log(
                '[PlatformHeader] Auth dependencies fully loaded (pfUser + orgs), skipping',
            );
            setLoading(false);
            return;
        }

        // If pfUser is missing but orgs are cached, still proceed to reload pfUser
        if (!pfUser?.user_uuid) {
            console.log(
                '[PlatformHeader] pfUser missing (page reload), reloading auth data from API',
            );
        }

        let cancelled = false;

        const loadAuthDependencies = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log('[PlatformHeader] Loading auth dependencies...');

                // ═══════════════════════════════════════════════════════════
                // DEPENDENCY 1: Org-PFUser Associations
                // @Manage=5: GET_USER_ASSOCIATIONS (active associations where Status=3)
                // 🔒 NOTE: Backend extracts pfUser from JWT token (no need to pass PfuserUuid)
                // ═══════════════════════════════════════════════════════════
                const orgResponse = await apiRequest('/api/org-pfuser-association-master', 'POST', {
                    manage: 5,
                    // PfuserUuid omitted - backend extracts from JWT token
                    ttp_id: 0,
                });

                if (cancelled) return;

                console.log('[PlatformHeader] Org associations loaded:', orgResponse);
                console.log(
                    '[PlatformHeader] Response type:',
                    typeof orgResponse,
                    'Is Array?',
                    Array.isArray(orgResponse),
                );

                // Transform to Organization format for Redux
                const associations: OrgAssociation[] = Array.isArray(orgResponse)
                    ? orgResponse
                    : (orgResponse as any)?.data || [];

                console.log('[PlatformHeader] Associations extracted:', associations);
                console.log('[PlatformHeader] Associations count:', associations.length);
                console.log('[PlatformHeader] First association sample:', associations[0]);

                // ✅ Filter by status_id = 3 (authenticated association)
                const orgs: Organization[] = associations
                    .filter((a) => {
                        console.log(
                            '[PlatformHeader] Filtering association:',
                            a.organization_uuid,
                            'Status:',
                            a.org_pfuser_association_status_id,
                            'Type:',
                            typeof a.org_pfuser_association_status_id,
                        );
                        return a.org_pfuser_association_status_id === 3;
                    })
                    .map((a) => ({
                        organization_uuid: a.organization_uuid,
                        organization_name: a.dba_name || a.legal_name || 'Unnamed Organization',
                        dba_name: a.dba_name,
                        role: '',
                        is_organization_admin: a.is_organization_admin || false,
                        is_organization_superadmin: a.is_organization_superadmin || false,
                        is_tg_superadmin: false,
                    }));

                console.log('[PlatformHeader] Transformed organizations:', orgs);
                console.log('[PlatformHeader] Organizations count:', orgs.length);

                // Store in Redux
                dispatch(setUserOrganizations(orgs));

                // ═══════════════════════════════════════════════════════════
                // DEPENDENCY 2: Org-PFUser Role Assignments
                // ═══════════════════════════════════════════════════════════
                const roleResponse = await apiRequest('/api/tsx/10220/2', 'POST', {
                    manage: 5,
                    // PfuserUuid omitted - backend extracts from JWT token
                });

                if (cancelled) return;

                console.log('[PlatformHeader] Role assignments loaded:', roleResponse);

                // Transform to RoleAssignment format for Redux
                const roleAssignments: RoleAssignment[] = Array.isArray(roleResponse)
                    ? roleResponse
                    : (roleResponse as any)?.data || [];

                // Store in Redux
                dispatch(setUserRoleAssignments(roleAssignments));

                // ═══════════════════════════════════════════════════════════
                // DEPENDENCY 3: TG Platform SuperAdmin Status Check
                // Dedicated TGAUTH.Auth_TGSuperAdmins table (not org-coupled)
                // @Manage=6: CHECK_USER (returns IsSuperAdmin: 1/0 from SP)
                // ═══════════════════════════════════════════════════════════
                try {
                    console.log(
                        '[PlatformHeader] Checking SuperAdmin status for:',
                        pfUser?.user_uuid,
                    );
                    const superAdminResponse = await apiRequest(
                        '/api/auth-superadmin-registry',
                        'POST',
                        {
                            manage: 6,
                            pfuser_uuid: pfUser?.user_uuid,
                        },
                    );

                    console.log('[PlatformHeader] SuperAdmin API response:', superAdminResponse);

                    if (
                        !cancelled &&
                        Array.isArray(superAdminResponse) &&
                        superAdminResponse.length > 0
                    ) {
                        // ✅ SP returns IsSuperAdmin column → normalize_row() → issuperadmin
                        const isSA =
                            superAdminResponse[0]?.issuperadmin === true ||
                            superAdminResponse[0]?.issuperadmin === 1;
                        console.log(
                            '[PlatformHeader] ✅ Platform SuperAdmin status:',
                            isSA,
                            'from value:',
                            superAdminResponse[0]?.issuperadmin,
                        );
                        dispatch(setSuperAdminStatus(isSA));
                    } else {
                        // Empty array = user not in SuperAdmin table
                        console.log(
                            '[PlatformHeader] ❌ User is not a Platform SuperAdmin (empty response)',
                        );
                        dispatch(setSuperAdminStatus(false));
                    }
                } catch (superAdminErr) {
                    // Non-critical error - user may not be SuperAdmin or table may not exist yet
                    console.warn(
                        '[PlatformHeader] Could not check Platform SuperAdmin status:',
                        superAdminErr,
                    );
                    dispatch(setSuperAdminStatus(false));
                }

                // ✅ DEFAULT TO "ALL ORGANIZATIONS" (user can filter later)
                if (!activeOrganizationUUID) {
                    // Check localStorage for cached selection
                    const storedOrgUuid = localStorage.getItem('active_organization_uuid');

                    if (storedOrgUuid && storedOrgUuid === 'ALL') {
                        // User previously selected "All Organizations"
                        console.log('[PlatformHeader] Restoring "All Organizations" from cache');
                        dispatch(setActiveOrganizationUUID('ALL'));
                    } else if (
                        storedOrgUuid &&
                        orgs.find((o) => o.organization_uuid === storedOrgUuid)
                    ) {
                        // User previously selected specific org (and it still exists)
                        console.log('[PlatformHeader] Restoring cached org:', storedOrgUuid);
                        dispatch(setActiveOrganizationUUID(storedOrgUuid));
                    } else {
                        // First time or cache invalid -> default to "All Organizations"
                        console.log('[PlatformHeader] Defaulting to "All Organizations"');
                        dispatch(setActiveOrganizationUUID('ALL'));
                        localStorage.setItem('active_organization_uuid', 'ALL');
                    }
                }

                console.log('[PlatformHeader] Auth dependencies loaded successfully');
            } catch (err) {
                if (!cancelled) {
                    console.error('[PlatformHeader] Error loading auth dependencies:', err);

                    // Enhanced error diagnostics
                    let errorMessage = 'Failed to load authentication data';
                    if (err instanceof Error) {
                        errorMessage = err.message;

                        // Capture specific API error details
                        if ((err as any).response) {
                            const status = (err as any).response.status;
                            const data = (err as any).response.data;

                            console.error('[PlatformHeader] API Error Details:', {
                                status,
                                data,
                                endpoint: (err as any).config?.url,
                                user_uuid: pfUser?.user_uuid || 'not_loaded',
                            });

                            if (status === 400) {
                                errorMessage = `400 Bad Request - Check if user UUID is valid: ${pfUser?.user_uuid || 'unknown'}`;
                            } else if (status === 401) {
                                errorMessage = '401 Unauthorized - JWT token may be expired';
                            } else if (status === 403) {
                                errorMessage = '403 Forbidden - User lacks required permissions';
                            } else if (status === 500) {
                                errorMessage = '500 Server Error - Backend API issue';
                            }
                        }
                    }

                    setError(errorMessage);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadAuthDependencies();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, pfUser, dispatch]); // ✅ REMOVED: userOrganizations.length (caused re-runs after data loaded)

    /**
     * Handle org selection change
     * Updates Redux + localStorage for persistence (cached default)
     */
    const handleOrgChange = (_event: any, newValue: Organization | null) => {
        if (newValue) {
            dispatch(setActiveOrganizationUUID(newValue.organization_uuid));
            localStorage.setItem('active_organization_uuid', newValue.organization_uuid);

            // Reset role selection when org changes
            dispatch(setActiveOrgRoleId(null));
            localStorage.removeItem('active_org_role_id');
        }
    };

    /**
     * Handle role selection change
     * Updates Redux + localStorage for persistence (cached default)
     * Special case: orgrole_id = -1 means "All User OrgRoles" (default)
     */
    const handleRoleChange = (_event: any, newValue: RoleAssignment | null) => {
        if (newValue) {
            if (newValue.orgrole_id === -1) {
                // User selected "All User OrgRoles" -> clear role filter
                dispatch(setActiveOrgRoleId(null));
                localStorage.removeItem('active_org_role_id');
            } else {
                // User selected specific role
                dispatch(setActiveOrgRoleId(newValue.orgrole_id));
                localStorage.setItem('active_org_role_id', String(newValue.orgrole_id));
            }
        } else {
            dispatch(setActiveOrgRoleId(null));
            localStorage.removeItem('active_org_role_id');
        }
    };

    // ═══════════════════════════════════════════════════════════════
    // 🎯 DROPDOWN OPTIONS WITH DEFAULTS (ALWAYS SHOW SOMETHING)
    // ═══════════════════════════════════════════════════════════════

    // Organization dropdown options: "All Organizations" + user's orgs
    const orgDropdownOptions: Organization[] = [
        {
            organization_uuid: 'ALL',
            organization_name:
                userOrganizations.length > 0 ? 'All Organizations' : 'No Org Associations',
            role: '',
        } as Organization,
        ...userOrganizations,
    ];

    const selectedOrg =
        orgDropdownOptions.find((o) => o.organization_uuid === activeOrganizationUUID) ||
        orgDropdownOptions[0]; // Default to "All Organizations"

    // Filter roles for active organization
    const orgRoles =
        activeOrganizationUUID && activeOrganizationUUID !== 'ALL'
            ? userRoleAssignments.filter((r) => r.organization_uuid === activeOrganizationUUID)
            : userRoleAssignments; // Show all roles if "All Organizations" selected

    // Role dropdown options: "All User OrgRoles" + filtered roles
    const roleDropdownOptions: (RoleAssignment & { isDefault?: boolean })[] = [
        {
            orgrole_id: -1,
            department: orgRoles.length > 0 ? 'All User OrgRoles' : 'No Roles Assigned',
            deptgroup: '',
            organization_uuid: '',
            isDefault: true,
        } as any,
        ...orgRoles,
    ];

    const selectedRole =
        roleDropdownOptions.find((r) => r.orgrole_id === activeOrgRoleId) || roleDropdownOptions[0]; // Default to "All User OrgRoles"

    // If not authenticated, show minimal loading state
    // 🔒 ARCHITECTURE: This checks authentication status, NOT data loading
    //    - showLoadingState = user not authenticated yet (JWT not validated)
    //    - loading = API call in progress (fetching org associations)
    //    - These are independent: user can be authenticated while data loads
    // 🐛 FIX (2026-07-01): Removed || !pfUser check - pfUser is null on page reload,
    //    blocking dropdowns forever. pfUser populated by API call after auth succeeds.
    const showLoadingState = !isAuthenticated;

    // Toggle between collapsed handle bar and expanded full controls
    const toggleCollapse = () => {
        setIsExpanded(!isExpanded);
    };

    const handleTimeclockClick = () => {
        setTimeStudyDashboardOpen(true); // Open TimeStudy dashboard overlay
    };

    const handleErrorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setErrorMenuAnchor(event.currentTarget);
    };

    const handleErrorMenuClose = () => {
        setErrorMenuAnchor(null);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    const handleLogout = () => {
        handleProfileMenuClose();
        dispatch(logoutAction());
        navigate('/login');
    };

    const handleOpenResetPassword = () => {
        handleProfileMenuClose();
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setResetPasswordError(null);
        setResetPasswordOpen(true);
    };

    const handleCloseResetPassword = () => {
        setResetPasswordOpen(false);
    };

    const handleResetPasswordSubmit = async () => {
        setResetPasswordError(null);

        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setResetPasswordError('All fields are required');
            return;
        }
        if (newPassword.length < 8) {
            setResetPasswordError('New password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setResetPasswordError('New password and confirmation do not match');
            return;
        }

        setResetPasswordSubmitting(true);
        try {
            await apiRequest('/api/auth/change-password', 'POST', {
                current_password: currentPassword,
                new_password: newPassword,
            });
            toast.success('Password updated successfully');
            setResetPasswordOpen(false);
        } catch (err: any) {
            setResetPasswordError(
                err?.response?.data?.detail || err?.message || 'Failed to update password',
            );
        } finally {
            setResetPasswordSubmitting(false);
        }
    };

    // Bypass the category menu when an error was already auto-detected (diagnosticsSlice) -
    // ask a single yes/no confirm instead of making the user re-pick a category we already know.
    //
    // Gating (2026-09-14): a REAL, auto-detected bug is open to everyone - anyone hitting an
    // actual error should be able to report it. Manually opening the menu (no detected error)
    // is gated to SuperAdmin for now, since that path also exposes feature-request/enhancement
    // options that bypass writing a dev backlog task directly. Future: classify by alpha-tester/
    // PM/marketing role instead of a flat SuperAdmin check.
    const handleBugIconClick = (event: React.MouseEvent<HTMLElement>) => {
        if (activeError) {
            setConfirmReportOpen(true);
            return;
        }
        if (!isSuperAdmin) {
            setHelpMenuOpen(true);
            return;
        }
        handleErrorMenuOpen(event);
    };

    const handleConfirmReportCancel = () => {
        setConfirmReportOpen(false);
    };

    const handleConfirmReportYes = async () => {
        setConfirmReportOpen(false);
        if (!activeError) return;
        // Component Registry Viewer previews components in a modal dialog without changing
        // the real browser route - moduleSeq/tabSeq below describe the HOST tab (e.g. Registry
        // Manager), not the component actually being previewed. Surface previewSeq separately
        // so triage isn't misled into thinking the host tab itself is broken (see
        // componentPreviewContext.ts and /memories/repo notes, 2026-10-07).
        const previewNote = activeError.previewSeq
            ? `\n\n⚠️ Reported while previewing component seq=${activeError.previewSeq}${activeError.previewFriendlyName ? ` (${activeError.previewFriendlyName})` : ''} via Component Registry Viewer. The failing endpoint belongs to that previewed component, not the host module/tab below.`
            : '';
        await submitBacklogTask({
            errorType: activeError.errorType,
            statusCode: activeError.statusCode,
            description: `Auto-detected via API failure.\n\nMessage: ${activeError.message}${previewNote}`,
            agenticFunctionId: 129, // SMOKE_TEST_BUG_REPORT
            priority: activeError.errorType === '500 Error' ? 1 : 2,
            moduleSeq: activeError.moduleSeq,
            tabSeq: activeError.tabSeq,
            route: activeError.route,
        });
        dispatch(clearActiveError());
    };

    // Manual "Report Error / Issue" menu selections (500/400/Theme/Data Binding)
    const handleErrorReport = async (errorType: string) => {
        handleErrorMenuClose();

        const { route, moduleSeq, tabSeq } = captureRouteContext();

        // Priority: 1=critical...5=lowest (Agentic_Task_Backlog scale)
        const priorityByType: Record<string, number> = {
            '500 Error': 1,
            '400 Error': 2,
            'Theme Import': 3,
            'Data Binding Lego Block': 2,
        };

        await submitBacklogTask({
            errorType,
            statusCode: null, // Manual category pick - no real HTTP status captured, unlike the auto-detected path
            description: `Reported via header bug widget while smoke-testing.`,
            agenticFunctionId: 129, // SMOKE_TEST_BUG_REPORT
            priority: priorityByType[errorType] ?? 3,
            moduleSeq,
            tabSeq,
            route,
        });
    };

    // "Feature Request / UI Change" - free-text asks like "add a field to the grid" or
    // "change the label on this column/button" - not an error, so uses a different
    // Agentic_Function (VOICE_FEATURE) and lower default priority.
    const handleFeatureRequestOpen = () => {
        handleErrorMenuClose();
        setFeatureRequestText('');
        setFeatureRequestOpen(true);
    };

    const handleFeatureRequestCancel = () => {
        setFeatureRequestOpen(false);
        setFeatureRequestText('');
    };

    const handleFeatureRequestSubmit = async () => {
        const description = featureRequestText.trim();
        if (!description) return;
        setFeatureRequestOpen(false);
        const { route, moduleSeq, tabSeq } = captureRouteContext();

        await submitBacklogTask({
            errorType: 'Feature Request',
            statusCode: null,
            description,
            agenticFunctionId: 103, // VOICE_FEATURE (Voice-Driven Feature Request)
            priority: 4,
            moduleSeq,
            tabSeq,
            route,
            titleOverride: description,
        });
        setFeatureRequestText('');
    };

    interface BacklogTaskParams {
        errorType: string;
        statusCode: number | null;
        description: string;
        agenticFunctionId: number;
        priority: number;
        moduleSeq: number | null;
        tabSeq: number | null;
        route: string;
        titleOverride?: string;
    }

    const submitBacklogTask = async ({
        errorType,
        statusCode,
        description,
        agenticFunctionId,
        priority,
        moduleSeq,
        tabSeq,
        route,
        titleOverride,
    }: BacklogTaskParams) => {
        // 'ALL' is a Redux UI sentinel for "All Organizations" selected - NOT a real GUID.
        // Must convert to null before sending, otherwise Pydantic's Optional[UUID] rejects it
        // (422 client error), which surfaced to the user as "Failed to log error report".
        const orgUuidParam = activeOrganizationUUID === 'ALL' ? null : activeOrganizationUUID;

        const taskTitle = titleOverride
            ? `[${errorType}] ${titleOverride.slice(0, 120)}`
            : `[${errorType}] Reported @ ${route}`;

        const context = {
            error_type: errorType,
            status_code: statusCode,
            component: 'PlatformHeader',
            route,
            module_seq: moduleSeq,
            tab_seq: tabSeq,
            timestamp: new Date().toISOString(),
            user_uuid: pfUser?.user_uuid,
            organization_uuid: orgUuidParam,
            user_agent: navigator.userAgent,
            screen_size: `${window.innerWidth}x${window.innerHeight}`,
        };

        try {
            await apiRequestBySeq(11028, 1, {
                manage: 1,
                agentic_function_id: agenticFunctionId,
                task_title: taskTitle,
                task_description: `${description}\n\nRoute: ${route}\nModule Seq: ${moduleSeq ?? 'N/A'}\nTab Seq: ${tabSeq ?? 'N/A'}`,
                input_specifications_json: JSON.stringify(context),
                task_status: 'PENDING',
                priority,
                requested_by_pfuser_uuid: pfUser?.user_uuid,
                organization_uuid: orgUuidParam,
                sequence_num: tabSeq ?? moduleSeq,
            });

            console.log(`[Backlog Task] ${errorType}:`, context);
            toast.success(`Logged: ${errorType} (Sequence_Num: ${tabSeq ?? moduleSeq ?? 'N/A'})`);
        } catch (err) {
            console.error('[Backlog Task] Failed to log:', err);
            toast.error(`Failed to log ${errorType}. See console for details.`);
        }
    };

    return (
        <Box
            sx={{
                height: isExpanded ? '56px' : '32px',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                px: 2,
                gap: 2,
                bgcolor: 'background.paper',
                transition: 'height 0.2s ease-in-out',
                flexShrink: 0,
            }}
        >
            {/* ══════ COLLAPSED STATE: Handle Bar with Org Chip ══════ */}
            {!isExpanded && (
                <>
                    <Tooltip title="Expand to change organization">
                        <IconButton size="small" onClick={toggleCollapse} sx={{ ml: -1 }}>
                            <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    {!isMarketingSite && !showLoadingState && selectedOrg && (
                        <Chip
                            label={selectedOrg.organization_name}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                                fontSize: '0.75rem',
                                height: '24px',
                            }}
                        />
                    )}

                    {showLoadingState && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Loading organizations...
                        </Typography>
                    )}

                    {!showLoadingState && error && (
                        <Tooltip title={error}>
                            <Chip
                                icon={<ErrorOutlineIcon />}
                                label="Auth Error"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{
                                    fontSize: '0.75rem',
                                    height: '24px',
                                }}
                            />
                        </Tooltip>
                    )}

                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip
                            title={
                                activeError
                                    ? `Detected: ${activeError.errorType} — click to report`
                                    : isSuperAdmin
                                      ? 'Report Error / Issue'
                                      : 'Need Help? Request Access'
                            }
                        >
                            <IconButton
                                size="small"
                                onClick={handleBugIconClick}
                                sx={{
                                    color: 'error.main',
                                    '&:hover': {
                                        bgcolor: 'error.light',
                                        color: 'error.contrastText',
                                    },
                                }}
                            >
                                <Badge color="error" variant="dot" invisible={!activeError}>
                                    <BugReportIcon
                                        fontSize="small"
                                        sx={
                                            activeError
                                                ? {
                                                      animation: 'pulse 1.2s infinite',
                                                      '@keyframes pulse': {
                                                          '0%': { opacity: 1 },
                                                          '50%': { opacity: 0.4 },
                                                          '100%': { opacity: 1 },
                                                      },
                                                  }
                                                : undefined
                                        }
                                    />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Need Help?">
                            <IconButton
                                size="small"
                                onClick={() => setHelpMenuOpen(true)}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Home">
                            <IconButton
                                size="small"
                                onClick={() => navigateToAppDefault(navigate)}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <HomeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Elevator">
                            <IconButton
                                size="small"
                                onClick={() => navigateToDirectory(2, navigate)}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <ElevatorIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="User Navigation">
                            <IconButton
                                size="small"
                                onClick={() => navigate(getEncodedRoute(10901))}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <MouseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="My Task">
                            <IconButton
                                size="small"
                                onClick={() => navigate(getEncodedRoute(10024))}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <AssignmentIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="My Tasks / Timeclock">
                            <IconButton size="small" onClick={handleTimeclockClick}>
                                <AccessTimeIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="MyTime">
                            <IconButton
                                size="small"
                                onClick={() => navigate(getEncodedRoute(10892))}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <PunchClockIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Phone / CollabCore">
                            <IconButton
                                size="small"
                                onClick={() => setPhoneToolOpen(true)}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <Badge color="error" badgeContent={unreadVoicemailCount} max={99}>
                                    <PhoneIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Chat">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    openChat({
                                        organization_uuid: activeOrganizationUUID || undefined,
                                        pfuser_uuid: pfUser?.user_uuid,
                                    })
                                }
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'primary.light',
                                    },
                                }}
                            >
                                <Badge color="error" badgeContent={unreadChatCount} max={99}>
                                    <ChatIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        {/* SuperAdmin Indicator - Green Checkmark */}
                        {isSuperAdmin && (
                            <Tooltip title="TG SuperAdmin">
                                <VerifiedUserIcon
                                    sx={{
                                        fontSize: 20,
                                        color: 'success.main',
                                        ml: 0.5,
                                        mr: 0.5,
                                    }}
                                />
                            </Tooltip>
                        )}

                        {/* User Avatar - Click to navigate to My Organizations & Settings.
                            JWT is the actual auth source of truth, so the display name is
                            surfaced as a tooltip rather than a visible label. Avatar will
                            show a real profile photo/thumbnail once that feature lands. */}
                        <Tooltip
                            title={`${pfUser?.username || 'Loading...'} — My Profile & Settings`}
                        >
                            <IconButton
                                size="small"
                                onClick={handleProfileMenuOpen}
                                sx={{ ml: 0.5 }}
                            >
                                {profilePhotoUrl ? (
                                    <Avatar src={profilePhotoUrl} sx={{ width: 24, height: 24 }} />
                                ) : (
                                    <AccountCircleIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </>
            )}

            {/* ══════ EXPANDED STATE: Full Controls ══════ */}
            {isExpanded && (
                <>
                    {/* Collapse Button - Always visible */}
                    <Tooltip title="Collapse header">
                        <IconButton size="small" onClick={toggleCollapse} sx={{ ml: -1 }}>
                            <ExpandLessIcon />
                        </IconButton>
                    </Tooltip>

                    <>
                        {/* Eager-mount layout: dropdowns/icons ALWAYS mount once expanded.
                            Loading state is reflected via disabled/loading props on the
                            controls themselves, not by unmounting them (eager-mount pattern). */}
                        {showLoadingState && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                Authenticating...
                            </Typography>
                        )}

                        {/* Organization + OrgRole Selectors - hidden on marketing site routes
                            (mkt_*), which have no organization/role context. */}
                        {!isMarketingSite && (
                            <>
                                {/* Organization Selector - ALWAYS SHOWS OPTIONS */}
                                <Autocomplete
                                    sx={{ minWidth: 280, maxWidth: 350 }}
                                    size="small"
                                    options={orgDropdownOptions}
                                    getOptionLabel={(option) => option.organization_name}
                                    value={selectedOrg}
                                    onChange={handleOrgChange}
                                    loading={loading || showLoadingState}
                                    disabled={loading || showLoadingState}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Organization"
                                            error={!!error}
                                            helperText={error}
                                            size="small"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body2">
                                                    {option.organization_name}
                                                </Typography>
                                                {option.role && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {option.role}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                />

                                {/* OrgRole Selector - ALWAYS SHOWS OPTIONS */}
                                <Autocomplete
                                    sx={{ minWidth: 280, maxWidth: 350 }}
                                    size="small"
                                    options={roleDropdownOptions}
                                    getOptionLabel={(option) => {
                                        if (option.isDefault)
                                            return option.department || 'All User OrgRoles';
                                        const parts = [];
                                        if (option.department) parts.push(option.department);
                                        if (option.deptgroup) parts.push(option.deptgroup);
                                        return parts.length > 0
                                            ? parts.join(' - ')
                                            : `Role ${option.orgrole_id}`;
                                    }}
                                    value={selectedRole}
                                    onChange={handleRoleChange}
                                    disabled={loading || showLoadingState}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Role / Department"
                                            placeholder={
                                                selectedOrg ? 'Select role...' : 'Select org first'
                                            }
                                            size="small"
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props}>
                                            <Box>
                                                <Typography variant="body2">
                                                    {option.department || 'Unnamed Department'}
                                                </Typography>
                                                {option.deptgroup && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {option.deptgroup}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </li>
                                    )}
                                />

                                {/* Active Role Chip - Only show if specific role selected */}
                                {selectedRole && selectedRole.orgrole_id !== -1 && (
                                    <Chip
                                        label={selectedRole.department || 'Role'}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                )}
                            </>
                        )}

                        {/* User Info */}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip
                                title={
                                    activeError
                                        ? `Detected: ${activeError.errorType} — click to report`
                                        : isSuperAdmin
                                          ? 'Report Error / Issue'
                                          : 'Need Help? Request Access'
                                }
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleBugIconClick}
                                    sx={{
                                        color: 'error.main',
                                        '&:hover': {
                                            bgcolor: 'error.light',
                                            color: 'error.contrastText',
                                        },
                                    }}
                                >
                                    <Badge color="error" variant="dot" invisible={!activeError}>
                                        <BugReportIcon
                                            fontSize="small"
                                            sx={
                                                activeError
                                                    ? {
                                                          animation: 'pulse 1.2s infinite',
                                                          '@keyframes pulse': {
                                                              '0%': { opacity: 1 },
                                                              '50%': { opacity: 0.4 },
                                                              '100%': { opacity: 1 },
                                                          },
                                                      }
                                                    : undefined
                                            }
                                        />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Need Help?">
                                <IconButton
                                    size="small"
                                    onClick={() => setHelpMenuOpen(true)}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Home">
                                <IconButton
                                    size="small"
                                    onClick={() => navigateToAppDefault(navigate)}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <HomeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Elevator">
                                <IconButton
                                    size="small"
                                    onClick={() => navigateToDirectory(2, navigate)}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <ElevatorIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="User Navigation">
                                <IconButton
                                    size="small"
                                    onClick={() => navigate(getEncodedRoute(10901))}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <MouseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="My Task">
                                <IconButton
                                    size="small"
                                    onClick={() => navigate(getEncodedRoute(10024))}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <AssignmentIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="My Tasks / Timeclock">
                                <IconButton size="small" onClick={handleTimeclockClick}>
                                    <AccessTimeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="MyTime">
                                <IconButton
                                    size="small"
                                    onClick={() => navigate(getEncodedRoute(10892))}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <PunchClockIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Phone / CollabCore">
                                <IconButton
                                    size="small"
                                    onClick={() => setPhoneToolOpen(true)}
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <Badge
                                        color="error"
                                        badgeContent={unreadVoicemailCount}
                                        max={99}
                                    >
                                        <PhoneIcon fontSize="small" />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Chat">
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        openChat({
                                            organization_uuid: activeOrganizationUUID || undefined,
                                            pfuser_uuid: pfUser?.user_uuid,
                                        })
                                    }
                                    sx={{
                                        color: 'primary.main',
                                        '&:hover': {
                                            bgcolor: 'primary.light',
                                        },
                                    }}
                                >
                                    <Badge color="error" badgeContent={unreadChatCount} max={99}>
                                        <ChatIcon fontSize="small" />
                                    </Badge>
                                </IconButton>
                            </Tooltip>

                            {/* SuperAdmin Indicator - Green Checkmark */}
                            {isSuperAdmin && (
                                <Tooltip title="TG SuperAdmin">
                                    <VerifiedUserIcon
                                        sx={{
                                            fontSize: 20,
                                            color: 'success.main',
                                            ml: 0.5,
                                            mr: 0.5,
                                        }}
                                    />
                                </Tooltip>
                            )}

                            {/* User Avatar - Click to navigate to My Organizations & Settings.
                                JWT is the actual auth source of truth, so the display name is
                                surfaced as a tooltip rather than a visible label. Avatar will
                                show a real profile photo/thumbnail once that feature lands. */}
                            <Tooltip
                                title={`${pfUser?.username || 'Loading...'} — My Profile & Settings`}
                            >
                                <IconButton
                                    size="small"
                                    onClick={handleProfileMenuOpen}
                                    sx={{ ml: 0.5 }}
                                >
                                    {profilePhotoUrl ? (
                                        <Avatar
                                            src={profilePhotoUrl}
                                            sx={{ width: 28, height: 28 }}
                                        />
                                    ) : (
                                        <AccountCircleIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </>

                    {!showLoadingState && error && (
                        <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <ErrorOutlineIcon color="error" />
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                            <Tooltip title="Click bug icon to report this error">
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    (Use 🐛 button to report)
                                </Typography>
                            </Tooltip>
                        </Box>
                    )}
                </>
            )}

            {/* Profile Avatar Dropdown Menu */}
            <Menu
                anchorEl={profileMenuAnchor}
                open={profileMenuOpen}
                onClose={handleProfileMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleProfileMenuClose();
                        navigate(getEncodedRoute(10028));
                    }}
                >
                    <AccountCircleIcon fontSize="small" sx={{ mr: 1.5 }} />
                    My Profile &amp; Settings
                </MenuItem>
                <MenuItem onClick={handleOpenResetPassword}>
                    <LockResetIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Reset Password
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Logout
                </MenuItem>
            </Menu>

            {/* Reset Password Dialog */}
            <Dialog
                open={resetPasswordOpen}
                onClose={handleCloseResetPassword}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Reset Password</DialogTitle>
                <DialogContent>
                    {resetPasswordError && (
                        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                            {resetPasswordError}
                        </Typography>
                    )}
                    <TextField
                        label="Current Password"
                        type="password"
                        fullWidth
                        size="small"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        label="New Password"
                        type="password"
                        fullWidth
                        size="small"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        size="small"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResetPassword} disabled={resetPasswordSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleResetPasswordSubmit}
                        variant="contained"
                        disabled={resetPasswordSubmitting}
                    >
                        {resetPasswordSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Reporting Context Menu */}
            <Menu
                anchorEl={errorMenuAnchor}
                open={errorMenuOpen}
                onClose={handleErrorMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => handleErrorReport('500 Error')}>🔴 500 Error</MenuItem>
                <MenuItem onClick={() => handleErrorReport('400 Error')}>
                    🟡 400(s) Client Error
                </MenuItem>
                <MenuItem onClick={() => handleErrorReport('Theme Import')}>
                    🎨 Theme Import Issue
                </MenuItem>
                <MenuItem onClick={() => handleErrorReport('Data Binding Lego Block')}>
                    🧱 Data Binding / Lego Block
                </MenuItem>
                <MenuItem onClick={handleFeatureRequestOpen}>
                    📝 Feature Request / UI Change
                </MenuItem>
            </Menu>

            {/* Confirm dialog - shown when clicking the icon while an auto-detected error is active */}
            <Dialog open={confirmReportOpen} onClose={handleConfirmReportCancel}>
                <DialogTitle>Report this as a bug fix request?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Detected: {activeError?.errorType}
                        <br />
                        {activeError?.message}
                        <br />
                        Route: {activeError?.route}
                        {activeError?.previewSeq && (
                            <>
                                <br />
                                ⚠️ Previewed via Component Registry Viewer: seq=
                                {activeError.previewSeq}
                                {activeError.previewFriendlyName
                                    ? ` (${activeError.previewFriendlyName})`
                                    : ''}
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmReportCancel}>Cancel</Button>
                    <Button onClick={handleConfirmReportYes} variant="contained" color="error">
                        Report It
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Feature request dialog - free-text asks: "add a field", "change this label", etc. */}
            <Dialog
                open={featureRequestOpen}
                onClose={handleFeatureRequestCancel}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Feature Request / UI Change</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 1 }}>
                        e.g. "Please add a Status field to this grid" or "Change the label on the
                        Submit button to Save".
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        value={featureRequestText}
                        onChange={(e) => setFeatureRequestText(e.target.value)}
                        placeholder="Describe the request..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFeatureRequestCancel}>Cancel</Button>
                    <Button
                        onClick={handleFeatureRequestSubmit}
                        variant="contained"
                        disabled={!featureRequestText.trim()}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* TimeStudy Dashboard Overlay - Conditionally rendered */}
            {timeStudyDashboardOpen && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        zIndex: 1300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                    onClick={() => setTimeStudyDashboardOpen(false)}
                >
                    <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            bgcolor: 'background.default',
                            borderRadius: 1,
                            maxWidth: 'xl',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                        }}
                    >
                        <TimeStudyDashboard />
                    </Box>
                </Box>
            )}

            {/* Chat Panel Overlay - single global instance (see chatSlice.ts / useChat.ts).
                Drawer from right side, elevated zIndex so it's never buried behind
                other Dialogs (Phone Tool, Meeting Master, etc.) that call openChat(). */}
            <ChatPanel
                open={isChatOpen}
                onClose={handleCloseChat}
                filterContext={
                    chatFilterContext || {
                        organization_uuid: activeOrganizationUUID || undefined,
                        pfuser_uuid: pfUser?.user_uuid,
                    }
                }
            />

            {/* Virtual Office Phone Overlay - self-contained Dialog, controlled from header */}
            <PhoneTool open={phoneToolOpen} onClose={handleClosePhoneTool} />

            {/* Help ATM Menu - shared self-service dialog, opened from the bug icon for
                non-SuperAdmin users (no auto-detected error). Same component the directory
                host's floating Help button uses - single source, no duplicated content. */}
            <AccessRequestModal
                open={helpMenuOpen}
                onClose={() => setHelpMenuOpen(false)}
                organizationUuid={activeOrganizationUUID || undefined}
            />
        </Box>
    );
};

export default PlatformHeader;
